import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Package, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const verifyPayment = async () => {
      // If no session_id, this is a non-Stripe order (Interac/COD)
      if (!sessionId) {
        setStatus('success');
        return;
      }

      try {
        const token = localStorage.getItem('token');
        
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 2000;

        const poll = async () => {
          try {
            const res = await axios.get(`${API_URL}/api/payments/status/${sessionId}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
              withCredentials: true
            });

            setPaymentDetails(res.data);

            if (res.data.payment_status === 'paid') {
              setStatus('success');
              return;
            }

            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, pollInterval);
            } else {
              setStatus('success'); // Assume success after max attempts
            }
          } catch (error) {
            console.error('Error polling payment status:', error);
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, pollInterval);
            } else {
              setStatus('error');
            }
          }
        };

        poll();
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
      }
    };

    verifyPayment();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center" data-testid="checkout-success-loading">
        <Loader2 className="h-16 w-16 text-brand-gold animate-spin mb-4" />
        <h1 className="font-heading text-3xl text-brand-black mb-4">Vérification du paiement...</h1>
        <p className="text-brand-dark/60">Veuillez patienter</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen pt-28 pb-20 bg-brand-white flex flex-col items-center justify-center">
        <div className="w-24 h-24 mx-auto mb-8 rounded-full bg-red-100 flex items-center justify-center">
          <XCircle className="h-12 w-12 text-red-500" />
        </div>
        <h1 className="font-heading text-3xl text-brand-black mb-4">Une erreur est survenue</h1>
        <p className="text-brand-dark/60 mb-6">Impossible de vérifier votre paiement</p>
        <Link to="/orders">
          <Button className="bg-brand-gold text-white hover:bg-brand-gold">
            Voir mes commandes
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 bg-brand-white flex items-center justify-center" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md mx-auto px-6"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring' }}
          className="w-24 h-24 mx-auto mb-8 rounded-full bg-green-100 flex items-center justify-center"
        >
          <CheckCircle className="h-12 w-12 text-green-500" />
        </motion.div>

        <h1 className="font-heading text-4xl text-brand-black mb-4">Merci pour votre commande !</h1>
        <p className="text-brand-dark/60 mb-8">
          {sessionId 
            ? 'Votre paiement a été confirmé. Vous recevrez un email de confirmation avec les détails de votre commande.'
            : 'Votre commande a été enregistrée avec succès.'}
        </p>

        {paymentDetails && paymentDetails.payment_status === 'paid' && (
          <div className="bg-brand-cream border border-brand-gold/20 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-heading text-lg text-brand-black mb-4">Détails du paiement</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-brand-dark/60">Statut</span>
                <span className="text-green-600 font-medium">Payé ✓</span>
              </div>
              <div className="flex justify-between">
                <span className="text-brand-dark/60">Montant</span>
                <span className="text-brand-black font-semibold">
                  {(paymentDetails.amount_total / 100).toFixed(2)} {paymentDetails.currency?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/orders">
            <Button className="bg-brand-gold text-white hover:bg-brand-gold px-8">
              <Package className="mr-2 h-4 w-4" /> Voir mes commandes
            </Button>
          </Link>
          <Link to="/products">
            <Button variant="outline" className="border-brand-gold/30 text-brand-black hover:bg-brand-cream px-8">
              Continuer mes achats
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
